import { Button, Flex, Text, useMantineTheme } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AGGridDefault,
  Pagination,
  SegmentControlRounded,
  Select,
} from "../../../components";
import { ColDef } from "ag-grid-community";
import { optionsTwo } from "../mock";
import { useQueryClient } from "@tanstack/react-query";
import { useMediaQuery } from "@mantine/hooks";
import MultiSelectProducts from "../components/MultiSelectProducts";
import { getPositionDownload, usePosition } from "../../../hooks/usePositions";
import { IQueryPosition } from "../../../interfaces/IQuery";
import { useAuth } from "../../../context/auth";
import { ERole } from "../../../interfaces/User/newUser";
import { useNotificationControl } from "../../../context/notificationContext";
import ButtonExport from "../components/ButtonExport";
import {
  countdownDate,
  endDateToApiString,
  startDateToApiString,
} from "../../../utils/date";
import { useTranslation } from "react-i18next";
import HeadingOperation from "../components/HeadingOperation";
import ModalRangeDate from "../components/ModalRangeDate";
import { TagCapsules } from "../../../components";
import IconInfo from "../components/IconInfo";
import { IColor } from "../../../utils/selectColors";
import { EOrderSide } from "../../../interfaces/IQuery";
import dataProcessing from "../../../utils/dataProcessing";
import { convertToDate } from "../../../utils/date";
import { EColorIconInfo } from "../../../interfaces/OrderPostions";
import { ReceiptSearch } from "iconsax-react";
import { client_selected } from "../../../client";
import ModalDetails from "./ModalDetails";

export default function OperationPosition() {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const queryClient = useQueryClient();
  const { notificationControl } = useNotificationControl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { roleSelected, user } = useAuth();
  const userCreatedAt = user?.created_at;
  const isAnalytic = roleSelected === ERole.analyst;
  const isLargerThan780 = useMediaQuery("(min-width: 780px)");
  const id = searchParams.get("productId");
  const [isLoadingDownload, setIsLoadingDownload] = useState<boolean>(false);
  const [IdDetails, setIdDetails] = useState<string>("");

  function dataRole() {
    return [
      {
        name: ERole.analyst,
        days: 30 - 1,
        isSelected: roleSelected === ERole.analyst,
      },
      {
        name: ERole.client,
        days: countdownDate(userCreatedAt),
        isSelected: roleSelected === ERole.client,
      },
    ];
  }

  const dataRoleSelected =
    dataRole().find((d) => d.isSelected) || dataRole()[1];

  const defaultQuery: IQueryPosition = {
    page: 1,
    pageSize: 10,
    open_date_start: new Date(
      new Date().setDate(new Date().getDate() - dataRoleSelected.days)
    ),
    open_date_end: new Date(),
    roleSelected: roleSelected,
    productId: !!id ? [Number(id)] : undefined,
  };

  const [query, setQuery] = useState<IQueryPosition>({
    ...defaultQuery,
    productId: !!id ? [Number(id)] : undefined,
  });

  useEffect(() => {
    setQuery((prev) => ({
      ...prev,
      roleSelected: roleSelected,
      open_date_start: new Date(
        new Date().setDate(new Date().getDate() - countdownDate(userCreatedAt))
      ),
    }));
  }, [roleSelected, userCreatedAt]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
      if (!value) return;
      if (key === "open_date_start") {
        const str = startDateToApiString(value as Date);
        if (str) newSearchParams.set(key, str);
      } else if (key === "open_date_end") {
        const str = endDateToApiString(value as Date);
        if (str) newSearchParams.set(key, str);
      } else if (Array.isArray(value)) {
        newSearchParams.set(key, value.join(","));
      } else {
        newSearchParams.set(key, String(value));
      }
    });

    navigate(`/operacoes/posicoes?${newSearchParams.toString()}`, {
      replace: true,
    });

    if (searchParams.size === 0) {
      navigate(`/operacoes/posicoes?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  }, [query, navigate, searchParams]);

  const { data: dataOrderPosition, isLoading, isFetching } = usePosition(query);
  // if (isError && isFetched && !data) return <PageNotFound />;

  const handleUpdate = () => {
    queryClient.refetchQueries({
      queryKey: [
        "position",
        query.order_status,
        query.order_side,
        query.is_open,
        query.page,
        query.pageSize,
        query.productId,
        query.orderBy,
        query.sortOrder,
        query.open_date_start,
        query.open_date_end,
        query.client_id,
      ],
    });
  };

  const handleSelectIsOpen = (value: string) => {
    setQuery((prevQuery) => ({ ...prevQuery, is_open: value }));
  };

  const handleExportTable = async () => {
    setIsLoadingDownload(true);

    try {
      await getPositionDownload(query);
    } catch (error) {
      // console.log("🚀 ~ error:", error);
      notificationControl({
        message: t("operacoes.posicoes.error_export_table"),
        type: "danger",
      });
    } finally {
      setIsLoadingDownload(false);
    }
  };

  const handleClearFilters = () => {
    setQuery((prev) => ({ ...defaultQuery, productId: prev.productId }));
  };

  const handleOpenDetailsFromRow = (id: string) => {
    setIdDetails(id ? id : "");
  };

  const startDate = query.open_date_start
    ? new Date(query.open_date_start)
    : new Date();
  const endDate = query.open_date_end
    ? new Date(query.open_date_end)
    : new Date();

  const dateDifference =
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);

  const disabledExportTable =
    isLoadingDownload || dateDifference > dataRoleSelected?.days;

  const pagination = {
    page: query.page,
    pageSize: query.pageSize,
    totalElements: dataOrderPosition?.totalElements,
    totalPages: dataOrderPosition?.totalPages,
    view: dataOrderPosition?.data?.length || 0,
  };

  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: t("operacoes.posicoes.table.table_head.product_title"),
        field: "product_title",
        width: 200,
        suppressColumnsToolPanel: true,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.product_title),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.is_open"),
        field: "is_open",
        suppressColumnsToolPanel: true,
        width: 110,
        cellRenderer: (params: any) => {
          const isOpen: boolean = !!params.data?.is_open;
          const textStatus = isOpen
            ? t("global.status_open.open")
            : t("global.status_open.close");
          return textStatus ? (
            <TagCapsules border colorTag={isOpen ? IColor.green : IColor.red}>
              {textStatus}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.status_copy"),
        field: "no_filter_status_copy",
        suppressColumnsToolPanel: true,
        width: 200,
        cellRenderer: (params: any) => {
          const status: EColorIconInfo | undefined = params.data?.status_copy;
          const textStatus: string | undefined = params.data?.status_copy_text;
          return (
            <Flex gap={4} align="center">
              <IconInfo
                statusCopy={status || EColorIconInfo.WHITE}
                statusCopyText={
                  textStatus || t("operacoes.posicoes.not_information")
                }
              />
              <Text fz="12px" fw="600" mt="4px">
                {textStatus}
              </Text>
            </Flex>
          );
        },
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.seq_num"),
        field: "seq_num",
        suppressColumnsToolPanel: true,
        width: 110,
        cellRenderer: (params: any) => dataProcessing(params.data?.seq_num),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.symbol"),
        field: "symbol",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => dataProcessing(params.data?.symbol),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.side_opening"),
        field: "side_opening",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) => {
          const side: EOrderSide | undefined = params.data?.side_opening;
          const text: string | undefined = params.data?.side_opening_text;
          return text ? (
            <TagCapsules
              border
              colorTag={side === EOrderSide.SELL ? IColor.green : IColor.orange}
            >
              {text}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.available_quantity"),
        field: "available_quantity",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.available_quantity),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.average_price"),
        field: "average_price",
        suppressColumnsToolPanel: true,
        width: 150,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.average_price),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.buy_average_price"),
        field: "buy_average_price",
        suppressColumnsToolPanel: true,
        width: 170,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.buy_average_price),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.sell_average_price"),
        field: "sell_average_price",
        suppressColumnsToolPanel: true,
        width: 180,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.sell_average_price),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.profit_loss"),
        field: "profit_loss",
        suppressColumnsToolPanel: true,
        width: 140,
        cellRenderer: (params: any) => dataProcessing(params.data?.profit_loss),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.open_date"),
        field: "open_date",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => convertToDate(params.data?.open_date),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.close_date"),
        field: "close_date",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => convertToDate(params.data?.close_date),
      },

      {
        headerName: t("operacoes.details") || "Detalhes",
        colId: "no_filter_details",
        pinned: "right",
        editable: false,
        suppressMovable: true,
        width: 90,
        onCellClicked: (e: any) => {
          const idDetail = isAnalytic
            ? e?.data?.id
            : e?.data?.analyst_position_id;
          const idFormatted = idDetail?.toString();
          return handleOpenDetailsFromRow(idFormatted);
        },
        cellRenderer: () => (
          <ReceiptSearch size="30" color={client_selected.colors.primary[1]} />
        ),
        sortable: false,
      },
    ],
    [t]
  );

  const handleRowClick = (row: any) => {
    const idDetail = row?.id;
    navigate(
      `/operacoes/ordens?position_id=${idDetail}&open_date_start=${query.open_date_start}&open_date_end=${query.open_date_end}` +
        (!!query.client_id ? `&client_id=${query.client_id}` : "")
    );
  };

  return (
    <Flex
      justify="space-between"
      align="center"
      bg="basic.0"
      p={{ base: "12px", 991: 32 }}
      direction={"column"}
      gap="24px"
      wrap={{ base: "wrap", 1650: "nowrap" }}
      sx={{ borderRadius: "12px 12px 0 0" }}
    >
      <Flex
        w="100%"
        gap={24}
        direction={{ base: "column", 1700: "column" }}
        justify={"space-between"}
        align={"start"}
      >
        <Flex
          w="100%"
          gap={24}
          direction={{ base: "column", 780: "row" }}
          justify={"space-between"}
          align={"start"}
        >
          <HeadingOperation
            lastUpdated={dataOrderPosition?.lastUpdated}
            title={t("operacoes.posicoes.title")}
            handleUpdate={handleUpdate}
          />
          {isAnalytic ? null : (
            <ButtonExport
              isLoadingDownload={isLoadingDownload}
              disabledExportTable={disabledExportTable}
              onClick={handleExportTable}
              days={dataRoleSelected.days}
            />
          )}
        </Flex>
        <Flex
          w="100%"
          gap={24}
          wrap={{ base: "wrap", 1700: "nowrap" }}
          justify={{ base: "space-between", 1700: "end" }}
          align={"end"}
        >
          <ModalRangeDate query={query} setQuery={setQuery} />
          {isLargerThan780 ? (
            <SegmentControlRounded
              data={optionsTwo}
              value={query.is_open}
              label={t("operacoes.posicoes.position_status")}
              onChange={handleSelectIsOpen}
            />
          ) : (
            <Select
              data={optionsTwo}
              value={query.is_open || ""}
              placeholder={t("operacoes.posicoes.position_status")}
              label={t("operacoes.posicoes.position_status")}
              onChange={handleSelectIsOpen}
              w="100%"
            />
          )}
          <MultiSelectProducts query={query} setQuery={setQuery} />
          {/* {roleSelected === ERole.analyst ? (
            <FilterClient query={query} setQuery={setQuery} />
          ) : null} */}
          <Button
            variant="primaryTransparent"
            w="100%"
            maw={{ base: "100%", 780: "fit-content" }}
            onClick={handleClearFilters}
            loaderProps={{ color: theme.colors.primary[1] }}
            h="40px"
          >
            {t("global.button.clean_filters")}
          </Button>
        </Flex>
      </Flex>

      <AGGridDefault
        title={
          isAnalytic
            ? t("operacoes.posicoes.table.title_analyst")
            : t("operacoes.posicoes.table.title_client")
        }
        rowData={dataOrderPosition?.data || []}
        columnDefs={columnDefs}
        isLoading={isLoading || isFetching}
        pagination={
          <Pagination
            pagination={pagination}
            onChange={(page) => setQuery({ ...query, page: page })}
          />
        }
        onRowClick={handleRowClick}
        storageKey={`columns-positions-${isAnalytic ? "analyst" : "customer"}`}
        context={{ query, setQuery, hasFilters: true }}
      />
      {!!IdDetails ? (
        <ModalDetails
          query={query}
          id={IdDetails}
          setClose={() => setIdDetails("")}
        />
      ) : null}
    </Flex>
  );
}
